"use client";

import Link from "next/link";
import { ArrowRight, Layers, Cpu, BarChart3, Sparkles, Bot, Briefcase, Library, Building2, Scale, Globe, Shield, Zap } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";

interface HubLink {
  label: string;
  href: string;
  description: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet";
  metric?: string;
}

const hubs: HubLink[] = [
  { label: "Platform", href: "/platform", description: "Architecture, performance, security, deployment tiers", icon: "Layers", accent: "crimson", metric: "14 routes" },
  { label: "Capabilities", href: "/capabilities", description: "20 core capabilities across content, distribution, intelligence", icon: "Sparkles", accent: "purple", metric: "20 routes" },
  { label: "Engines", href: "/engines", description: "5 proprietary AI engines: OSE, NGE, CRE, ECA, AOE", icon: "Cpu", accent: "cyan", metric: "5 routes" },
  { label: "Services", href: "/services", description: "Brand, content, crisis, intelligence, media relations", icon: "Briefcase", accent: "emerald", metric: "60+ routes" },
  { label: "Analytics", href: "/analytics", description: "Dashboards, comparative, predictive, sentiment, conversion", icon: "BarChart3", accent: "violet", metric: "8 routes" },
  { label: "Autonomous", href: "/autonomous", description: "Adaptive response, decision triggers, persona networks", icon: "Bot", accent: "amber", metric: "8 routes" },
  { label: "Technology", href: "/technology", description: "AI/ML, NLP, infrastructure, real-time processing", icon: "Zap", accent: "denim", metric: "10 routes" },
  { label: "Resources", href: "/resources", description: "Documentation, guides, whitepapers, case studies, webinars", icon: "Library", accent: "emerald", metric: "17 routes" },
  { label: "Developers", href: "/developers/portal", description: "API docs, SDK, webhooks, integration guides, community", icon: "Code2", accent: "denim", metric: "7 routes" },
  { label: "Company", href: "/company", description: "Mission, history, leadership, careers, partners, press", icon: "Building2", accent: "violet", metric: "11 routes" },
  { label: "Legal", href: "/legal", description: "Privacy, terms, security, compliance, cookies, SLA", icon: "Scale", accent: "amber", metric: "8 routes" },
];

const iconMap: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  Layers, Cpu, BarChart3, Sparkles, Bot, Briefcase, Library, Building2, Scale, Globe, Shield, Zap,
};

export function SiteDirectory() {
  return (
    <Section id="directory" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/4 left-1/4 h-72 w-72 rounded-full bg-[var(--color-purple)] opacity-15 blur-[120px]" />
      </div>
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-purple)]">Complete Directory</span>
            <h2 className="mt-3">Every Surface. One Platform.</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">
              Perception X2 spans 11 categories, 170+ pages, and 50+ capabilities. This is the master directory — every hub, every page, every dimension of the platform.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {hubs.map((hub, i) => {
            const Icon = iconMap[hub.icon] ?? Layers;
            return (
              <Reveal key={hub.href} delay={i * 40}>
                <Link
                  href={hub.href}
                  className="group block glass rounded-2xl p-5 h-full hover:scale-[1.02] transition-transform relative overflow-hidden"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${hub.accent}) 12%, transparent)`,
                  }}
                >
                  <div
                    className="pointer-events-none absolute -top-12 -right-12 h-32 w-32 rounded-full opacity-15 blur-2xl"
                    style={{ background: `var(--color-${hub.accent})` }}
                  />
                  <div className="relative">
                    <div className="flex items-start justify-between mb-3">
                      <span
                        className="grid place-items-center h-10 w-10 rounded-lg shrink-0"
                        style={{
                          background: `color-mix(in oklch, var(--color-${hub.accent}) 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${hub.accent}) 35%, transparent)`,
                        }}
                      >
                        <Icon className="h-4 w-4" style={{ color: `var(--color-${hub.accent})` }} />
                      </span>
                      {hub.metric && (
                        <span
                          className="eyebrow-tiny font-bold"
                          style={{ color: `var(--color-${hub.accent})` }}
                        >
                          {hub.metric}
                        </span>
                      )}
                    </div>
                    <h3 className="text-base">{hub.label}</h3>
                    <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed line-clamp-2">
                      {hub.description}
                    </p>
                    <div className="mt-3 flex items-center gap-1 text-xs font-semibold" style={{ color: `var(--color-${hub.accent})` }}>
                      Browse hub <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}